import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import { format, subDays } from "date-fns";
import { X, Sparkles, TrendingUp, TrendingDown } from "lucide-react";
import { cn, formatINR } from "@/lib/utils";
import { CategoryPicker } from "@/components/categories/CategoryPicker";
import {
  normalizeCategoriesResponse,
  type Category,
} from "@/components/categories/categoryHelpers";
import { DescriptionTypeahead } from "./DescriptionTypeahead";
import { NumberPad } from "./NumberPad";
import { MobileEntryScreen } from "./MobileEntryScreen";
import { CategoryPickerSheet } from "./CategoryPickerSheet";
import { AccountPickerSheet } from "./AccountPickerSheet";
import {
  INITIAL_CALC_STATE,
  applyCalcKey,
  padKeyFromKeyboard,
  paiseToEntry,
  resolveCalcState,
  sanitizeAmountInput,
  parseAmountRupees,
  formatAmountForDisplay,
  type CalcKey,
  type CalcState,
} from "./amountPad";
import { reconcileAccountId } from "./reconcileAccountId";
import {
  useEntrySuggestions,
  type SuggestionTemplate,
} from "@/lib/hooks/use-entry-suggestions";
import { apiFetch } from "@/lib/api/client";
import { apiUrl } from "@/lib/api/config";
import { newEntryProblem } from "@core";

export const LAST_ACCOUNT_STORAGE_KEY = "aim-last-account-id";

const PAYMENT_METHODS = [
  { value: "UPI", label: "UPI" },
  { value: "CASH", label: "Cash" },
  { value: "CREDIT_CARD", label: "Credit Card" },
  { value: "DEBIT_CARD", label: "Debit Card" },
  { value: "BANK_TRANSFER", label: "Bank Transfer" },
  { value: "OTHER", label: "Other" },
] as const;

type EntryType = "income" | "expense";
type DateMode = "today" | "yesterday" | "pick";

interface QuickAddSheetProps {
  open: boolean;
  onClose: () => void;
  defaultType?: EntryType;
  /** Preselect this account on open (e.g. quick-adding from an account's own
   *  detail page) instead of the last-used one from localStorage. */
  defaultAccountId?: string;
}

interface AccountOption {
  id: string;
  bankName: string;
  nickname?: string | null;
  accountType?: string | null;
  color?: string | null;
}

/** Fields a save can be driven from directly, bypassing the form state. */
interface EntryOverride {
  amountRupees: number;
  categoryId: string;
  description: string;
  accountId: string;
  paymentMethod?: string;
}

function readLastAccountId(): string {
  if (typeof window === "undefined" || !window.localStorage) return "";
  try {
    return window.localStorage.getItem(LAST_ACCOUNT_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

function saveLastAccountId(id: string) {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    if (id) window.localStorage.setItem(LAST_ACCOUNT_STORAGE_KEY, id);
  } catch {
    // storage disabled — remembering the account is a nicety, never fatal
  }
}

/** True below Tailwind's `sm` breakpoint. Resolved post-mount (client only). */
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    // eslint-disable-next-line react-hooks/set-state-in-effect -- media query is client-only; post-mount sync is the standard pattern
    setIsMobile(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

/**
 * True on touch-primary devices (phones, tablets). Resolved post-mount, and
 * deliberately defaulting to `false`: the amount field is read-only from the
 * very first paint regardless, so the OS keyboard can never appear — the only
 * thing this gates is whether the on-screen pad is worth the vertical space.
 * A mouse-driven desktop drives the same field from the physical keyboard.
 *
 * Capability, not width: a landscape tablet is wider than `sm` but still has
 * no physical keyboard, and hiding the pad there would leave it unable to
 * enter an amount at all.
 */
function useHasCoarsePointer(): boolean {
  // Defaults to TRUE, deliberately. The amount input is read-only whenever the
  // pad is shown, so the two must never diverge — and if they do, the safe
  // direction is "pad visible on a device that didn't need it" rather than
  // "read-only input with no keyboard and no pad", which makes the amount
  // impossible to enter at all. Starting false meant every first render, and
  // every environment where matchMedia reports a fine pointer, produced a dead
  // field. Fail toward usable.
  const [coarse, setCoarse] = useState(true);
  useEffect(() => {
    const mq = window.matchMedia("(pointer: coarse)");
    // eslint-disable-next-line react-hooks/set-state-in-effect -- media query is client-only; post-mount sync is the standard pattern
    setCoarse(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setCoarse(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return coarse;
}

/**
 * QuickAdd — the fast-entry surface. Bottom sheet on mobile (spring slide-up,
 * drag-to-dismiss), centered dialog on ≥sm. Shares the suggestions endpoint
 * with the full forms via useEntrySuggestions.
 */
export function QuickAddSheet({
  open,
  onClose,
  defaultType = "expense",
  defaultAccountId,
}: QuickAddSheetProps) {
  const queryClient = useQueryClient();
  const shouldReduceMotion = useReducedMotion();
  const isMobile = useIsMobile();
  const showPad = useHasCoarsePointer();

  const [type, setType] = useState<EntryType>(defaultType);
  // The amount lives in the calculator state; `calc.entry` is the string the
  // plain text input used to hold, byte for byte.
  const [calc, setCalc] = useState<CalcState>(INITIAL_CALC_STATE);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const [dateMode, setDateMode] = useState<DateMode>("today");
  const [pickedDate, setPickedDate] = useState("");
  const [accountId, setAccountId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("UPI");
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [accountPickerOpen, setAccountPickerOpen] = useState(false);

  const amount = calc.entry;

  // Reset to a clean slate every time the sheet opens.
  useEffect(() => {
    if (!open) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- deliberate open-time reset of transient form state
    setType(defaultType);
    setCalc(INITIAL_CALC_STATE);
    setCategoryId(null);
    setDescription("");
    setDateMode("today");
    setPickedDate("");
    setAccountId(defaultAccountId || readLastAccountId());
    setPaymentMethod("UPI");
    setCategoryPickerOpen(false);
    setAccountPickerOpen(false);
  }, [open, defaultType, defaultAccountId]);

  // The mobile screen covers the viewport, but a fixed overlay does not stop
  // the page underneath from scrolling on a touch drag — which is exactly the
  // "why is this thing moving" behaviour a zero-scroll screen must not have.
  useEffect(() => {
    if (!open || !isMobile) return;
    // Both elements: `overflow:hidden` on <body> alone does not stop the
    // scroll when <html> is the scrolling element, which it is here.
    const html = document.documentElement;
    const previousHtml = html.style.overflow;
    const previousBody = document.body.style.overflow;
    html.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      html.style.overflow = previousHtml;
      document.body.style.overflow = previousBody;
    };
  }, [open, isMobile]);

  // Escape closes the sheet (typeahead swallows Escape first when open).
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const { data: accounts = [], isSuccess: accountsLoaded } = useQuery({
    queryKey: ["accounts", false],
    queryFn: async () => {
      const r = await apiFetch(apiUrl("accounts", "", { includeArchived: "false" }));
      if (!r.ok) return [];
      return r.json() as Promise<AccountOption[]>;
    },
    enabled: open,
  });

  // Reconcile the remembered account id once accounts load — a stale
  // localStorage id (account archived since, or another user's on a shared
  // device) would render like "No account" while still being POSTed.
  useEffect(() => {
    if (!accountsLoaded) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- validation depends on async query data
    setAccountId((cur) => reconcileAccountId(cur, accounts));
  }, [accountsLoaded, accounts]);

  const { data: frequent = [] } = useEntrySuggestions(type, "", open);

  const kind = type === "income" ? "INCOME" : "EXPENSE";
  // Same query key the pickers use, so this is one shared fetch — it exists
  // here only to resolve the selected category's name and colour for the
  // mobile tap target.
  const { data: categories = [] } = useQuery({
    queryKey: ["categories", kind],
    queryFn: async () => {
      const r = await apiFetch(apiUrl("categories", "", { kind, includeArchived: "true" }));
      if (!r.ok) return [] as Category[];
      return normalizeCategoriesResponse(await r.json());
    },
    enabled: open,
  });

  const date = useMemo(() => {
    const today = format(new Date(), "yyyy-MM-dd");
    if (dateMode === "today") return today;
    if (dateMode === "yesterday") return format(subDays(new Date(), 1), "yyyy-MM-dd");
    return pickedDate || today;
  }, [dateMode, pickedDate]);

  // Same rule the plain text input used (`parseFloat` > 0), lifted into a
  // named pure helper so the pad and a physical keyboard share one parse path.
  // `resolveCalcState` settles a half-typed expression first, so `250 + 130`
  // with `=` never pressed saves 380 rather than dropping the tail.
  const amountValue = parseAmountRupees(resolveCalcState(calc).entry);
  // F7: a new entry needs a remark and, under a parent, a sub-category.
  const entryProblem = categoryId
    ? newEntryProblem({ type, categoryId, remark: description }, categories)
    : null;
  const canSave = amountValue !== null && !!categoryId && !entryProblem;

  const mutation = useMutation({
    mutationFn: async (override?: EntryOverride) => {
      const rupees = override ? override.amountRupees : amountValue;
      const useCategoryId = override ? override.categoryId : categoryId;
      if (rupees === null || rupees === undefined) throw new Error("Enter an amount");
      // The one-tap template / suggestion path bypasses the form, so the F7
      // rule is checked on exactly what is about to be sent.
      const problem = newEntryProblem(
        {
          type,
          categoryId: useCategoryId,
          remark: override ? override.description : description,
        },
        categories,
      );
      if (problem) throw new Error(problem.message);
      const url = type === "income" ? apiUrl("income") : apiUrl("expenses");
      // Belt-and-braces: never submit an account id that doesn't resolve to
      // a currently selectable account (see reconcileAccountId).
      const safeAccountId = reconcileAccountId(
        override ? override.accountId : accountId,
        accounts,
      );
      const body: Record<string, unknown> = {
        date,
        amount: rupees,
        categoryId: useCategoryId,
        description: (override ? override.description : description).trim() || undefined,
        accountId: safeAccountId || null,
      };
      if (type === "expense") {
        body.paymentMethod = override ? override.paymentMethod ?? paymentMethod : paymentMethod;
      }

      const res = await apiFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.error || "Failed to save entry");
      }
      return res.json();
    },
    onSuccess: () => {
      saveLastAccountId(reconcileAccountId(accountId, accounts));
      // "accounts" refreshes balances (detail page included via prefix match);
      // "account-ledger" refreshes the account statement the entry lands on.
      for (const key of ["income", "expenses", "expenses-all-for-stats", "dashboard", "records", "suggestions", "accounts", "account-ledger"]) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
      toast.success(type === "income" ? "Income added" : "Expense added");
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  /**
   * A template carries amount, category, account, description and payment
   * method — one tap fills all five, rather than only prefilling the
   * description as the chips used to.
   */
  function applyTemplate(t: SuggestionTemplate) {
    setCalc({ ...INITIAL_CALC_STATE, entry: paiseToEntry(t.amountPaise) });
    setCategoryId(t.categoryId);
    setDescription(t.description);
    if (t.accountId && reconcileAccountId(t.accountId, accounts)) {
      setAccountId(t.accountId);
    }
    if (t.paymentMethod) setPaymentMethod(t.paymentMethod);
  }

  /**
   * Fill *and* submit. The state setters above are async, so the payload is
   * built from the template directly rather than read back out of state.
   */
  function applyAndSaveTemplate(t: SuggestionTemplate) {
    applyTemplate(t);
    if (mutation.isPending) return;
    mutation.mutate({
      amountRupees: t.amountPaise / 100,
      categoryId: t.categoryId,
      description: t.description,
      accountId: t.accountId ?? "",
      paymentMethod: t.paymentMethod,
    });
  }

  function pressPadKey(key: CalcKey) {
    setCalc((cur) => applyCalcKey(cur, key));
  }

  /**
   * A physical keyboard drives the exact same reducer the pad does, so the
   * read-only field is fully usable on a desktop. Keys the pad does not own
   * fall through untouched — Enter still submits the form, Escape still
   * closes the sheet, Tab still moves on — as do clipboard/select shortcuts.
   */
  function onAmountKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const key = padKeyFromKeyboard(e.key);
    if (!key) return;
    e.preventDefault();
    pressPadKey(key);
  }

  function onAmountPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData("text");
    if (!text) return;
    // The field is read-only, so the browser would drop the paste entirely.
    e.preventDefault();
    setCalc((cur) => ({
      ...cur,
      entry: sanitizeAmountInput(text),
      justEvaluated: false,
    }));
  }

  const accent = type === "income" ? "emerald" : "rose";
  const selectedCategory = categories.find((c) => c.id === categoryId) ?? null;
  const selectedAccount = accounts.find((a) => a.id === accountId) ?? null;

  // Desktop only — the mobile slide-up now lives on `mobileScreen`, and the
  // drag-to-dismiss handle went with the bottom sheet it belonged to.
  const panelMotion = shouldReduceMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.1 } }
    : {
        initial: { opacity: 0, scale: 0.96, y: 12 },
        animate: { opacity: 1, scale: 1, y: 0 },
        exit: { opacity: 0, scale: 0.96, y: 12 },
        transition: { type: "spring" as const, damping: 26, stiffness: 320 },
      };

  // Mobile: the full-screen Wallet-style entry screen. Desktop keeps the
  // centered dialog below, unchanged.
  const mobileScreen = (
    <motion.div
      key="quickadd-mobile"
      initial={shouldReduceMotion ? { opacity: 0 } : { y: "100%" }}
      animate={shouldReduceMotion ? { opacity: 1 } : { y: 0 }}
      exit={shouldReduceMotion ? { opacity: 0 } : { y: "100%" }}
      transition={
        shouldReduceMotion
          ? { duration: 0.1 }
          : { type: "spring", damping: 32, stiffness: 340 }
      }
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Quick add entry"
    >
      <MobileEntryScreen
        type={type}
        onTypeChange={(t) => {
          setType(t);
          setCategoryId(null);
        }}
        calc={calc}
        onPadKey={pressPadKey}
        onAmountKeyDown={onAmountKeyDown}
        onAmountPaste={onAmountPaste}
        category={selectedCategory}
        onOpenCategoryPicker={() => setCategoryPickerOpen(true)}
        account={selectedAccount}
        onOpenAccountPicker={() => setAccountPickerOpen(true)}
        description={description}
        onDescriptionChange={setDescription}
        dateMode={dateMode}
        onDateModeChange={setDateMode}
        pickedDate={pickedDate}
        onPickedDateChange={setPickedDate}
        date={date}
        paymentMethod={paymentMethod}
        onPaymentMethodChange={setPaymentMethod}
        paymentMethods={PAYMENT_METHODS}
        suggestions={frequent}
        onApplyTemplate={applyTemplate}
        onApplyAndSaveTemplate={applyAndSaveTemplate}
        canSave={canSave}
        blockedReason={amountValue !== null ? entryProblem?.message ?? null : null}
        isSaving={mutation.isPending}
        onSave={() => {
          if (canSave && !mutation.isPending) mutation.mutate(undefined);
        }}
        onClose={onClose}
      />

      <CategoryPickerSheet
        open={categoryPickerOpen}
        kind={kind}
        value={categoryId}
        onSelect={setCategoryId}
        onClose={() => setCategoryPickerOpen(false)}
        suggestions={frequent}
      />
      <AccountPickerSheet
        open={accountPickerOpen}
        accounts={accounts}
        value={accountId}
        onSelect={setAccountId}
        onClose={() => setAccountPickerOpen(false)}
      />
    </motion.div>
  );

  const desktopDialog = (
    <div
      key="quickadd-desktop"
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Quick add entry"
    >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />

          {/* Panel */}
          <div className="pointer-events-none flex h-full items-end justify-center sm:items-center sm:p-4">
            <motion.div
              {...panelMotion}
              className={cn(
                "pointer-events-auto relative w-full overflow-y-auto border border-[var(--cp-line)] bg-[var(--cp-surface)]",
                "max-h-[92dvh] rounded-t-[14px] p-3 pb-[max(1rem,env(safe-area-inset-bottom))]",
                "sm:max-w-md sm:rounded-[14px] sm:p-6 sm:shadow-[0_24px_60px_rgba(15,23,42,.22)]",
              )}
            >
              {/* Header doubles as the Income/Expense segmented toggle — the
                  toggle already names the sheet, so a separate title row would
                  cost a line to repeat what the control says. */}
              <div className="mb-3 flex items-center gap-2">
                <h2 className="sr-only">Quick Add</h2>
                <div className="grid flex-1 grid-cols-2 gap-1 rounded-xl bg-[var(--cp-surface-2)] p-1">
                  {(["income", "expense"] as const).map((t) => {
                    const active = type === t;
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => {
                          setType(t);
                          setCategoryId(null);
                        }}
                        aria-pressed={active}
                        className={cn(
                          "relative flex min-h-10 items-center justify-center gap-1.5 rounded-lg text-sm font-medium transition-colors",
                          active
                            ? t === "income"
                              ? "text-[var(--cp-emerald)]"
                              : "text-[var(--cp-rose-ink)]"
                            : "text-[var(--cp-ink-3)] hover:text-[var(--cp-ink-2)]",
                        )}
                      >
                        {active && (
                          <motion.span
                            layoutId="quickadd-type-pill"
                            transition={
                              shouldReduceMotion
                                ? { duration: 0 }
                                : { type: "spring", damping: 30, stiffness: 400 }
                            }
                            className="absolute inset-0 rounded-lg bg-[var(--cp-surface)] shadow-sm"
                            aria-hidden
                          />
                        )}
                        <span className="relative flex items-center gap-1.5">
                          {t === "income" ? (
                            <TrendingUp className="h-4 w-4" />
                          ) : (
                            <TrendingDown className="h-4 w-4" />
                          )}
                          {t === "income" ? "Income" : "Expense"}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={onClose}
                  aria-label="Close"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-[var(--cp-ink-3)] transition-colors hover:bg-[var(--cp-surface-2)] hover:text-[var(--cp-ink-2)]"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (canSave && !mutation.isPending) mutation.mutate(undefined);
                }}
                className="space-y-3"
              >
                {/* Big amount input */}
                <div
                  className={cn(
                    // Vertical padding lives on the <input>, not here, so the
                    // input's own hit box stays 40px tall as the box shrinks.
                    "flex items-baseline gap-2 rounded-xl border-2 px-3 py-1 transition-colors",
                    accent === "emerald"
                      ? "border-[var(--cp-emerald-wash)] focus-within:border-[var(--cp-emerald)]"
                      : "border-[var(--cp-rose-wash)] focus-within:border-[var(--cp-rose)]",
                  )}
                >
                  <span className="text-xl font-semibold text-[var(--cp-ink-3)]">₹</span>
                  {/* Read-only on purpose: it suppresses the OS keyboard on
                      iOS and Android while staying focusable and labelled, so
                      the category and account controls below are never
                      covered. The keypad below and a physical keyboard both
                      drive it through the same reducer. */}
                  <input
                    type="text"
                    // Bound to showPad so they can NEVER diverge. With the pad
                    // present, read-only keeps the OS keyboard shut so category
                    // and account stay uncovered. Without the pad, the field
                    // falls back to a normal numeric input — otherwise there is
                    // no way to enter an amount at all.
                    inputMode={showPad ? "none" : "decimal"}
                    readOnly={showPad}
                    autoFocus
                    value={formatAmountForDisplay(amount)}
                    onKeyDown={onAmountKeyDown}
                    onPaste={onAmountPaste}
                    placeholder="0"
                    aria-label="Amount in rupees"
                    className="min-h-10 w-full bg-transparent py-1 text-2xl font-bold text-[var(--cp-ink)] caret-transparent placeholder:text-[var(--cp-ink-3)] placeholder:opacity-50 focus:outline-none"
                  />
                  {amount !== "" && (
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => pressPadKey("clear")}
                      aria-label="Clear amount"
                      className="flex h-10 w-10 shrink-0 items-center justify-center self-center rounded-lg text-[var(--cp-ink-3)] transition-colors hover:bg-[var(--cp-surface-2)] hover:text-[var(--cp-ink-2)]"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {/* Frequent templates */}
                {frequent.length > 0 && (
                  <div>
                    <p className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--cp-ink-3)]">
                      <Sparkles className="h-3 w-3" aria-hidden /> Frequent
                    </p>
                    <div className="flex gap-1.5 overflow-x-auto scrollbar-none">
                      {frequent.map((s) => (
                        <button
                          key={`${s.categoryId}-${s.description}`}
                          type="button"
                          onClick={() => applyTemplate(s)}
                          className="flex min-h-10 shrink-0 items-center gap-1.5 rounded-full border border-[var(--cp-line)] bg-[var(--cp-surface)] px-2.5 py-1.5 text-xs font-medium text-[var(--cp-ink-2)] transition-colors hover:border-[var(--cp-accent)] hover:bg-[var(--cp-surface-2)] active:scale-[0.97]"
                        >
                          <span
                            className="h-2 w-2 shrink-0 rounded-full"
                            style={{ backgroundColor: s.category.color ?? "#94a3b8" }}
                            aria-hidden
                          />
                          <span className="max-w-[9rem] truncate">{s.description}</span>
                          <span className="text-[var(--cp-ink-3)]">{formatINR(s.amountPaise)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Description + typeahead */}
                <DescriptionTypeahead
                  type={type}
                  value={description}
                  onChange={setDescription}
                  onSelectTemplate={applyTemplate}
                  placeholder="Remark (required) — type for suggestions"
                  inputClassName="border-[var(--cp-line)] bg-[var(--cp-surface)] text-[var(--cp-ink)] placeholder:text-[var(--cp-ink-3)] focus:border-[var(--cp-accent)]"
                />

                {/* Category */}
                <div>
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--cp-ink-3)]">
                    Category
                  </p>
                  <CategoryPicker
                    kind={type === "income" ? "INCOME" : "EXPENSE"}
                    value={categoryId}
                    onChange={setCategoryId}
                  />
                </div>

                {/* Date chips — the chip labels are the field label, so no
                    separate heading row is spent on them. */}
                <div role="group" aria-label="Date">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {(
                      [
                        ["today", "Today"],
                        ["yesterday", "Yesterday"],
                        ["pick", "Pick date"],
                      ] as const
                    ).map(([mode, label]) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setDateMode(mode)}
                        aria-pressed={dateMode === mode}
                        className={cn(
                          "min-h-10 rounded-full border px-2.5 py-1.5 text-xs font-medium transition-colors active:scale-[0.97]",
                          dateMode === mode
                            ? "border-[var(--cp-accent)] bg-[var(--cp-accent-wash)] text-[var(--cp-accent-ink)]"
                            : "border-[var(--cp-line)] bg-[var(--cp-surface)] text-[var(--cp-ink-2)] hover:bg-[var(--cp-surface-2)]",
                        )}
                      >
                        {label}
                      </button>
                    ))}
                    {dateMode === "pick" && (
                      <input
                        type="date"
                        value={pickedDate || date}
                        onChange={(e) => setPickedDate(e.target.value)}
                        aria-label="Entry date"
                        className="min-h-10 rounded-lg border border-[var(--cp-line)] bg-[var(--cp-surface)] px-3 py-2 text-sm text-[var(--cp-ink)]"
                      />
                    )}
                  </div>
                </div>

                {/* Account + payment method */}
                <div className={cn("grid gap-2", type === "expense" ? "grid-cols-2" : "grid-cols-1")}>
                  <div>
                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--cp-ink-3)]">
                      Account
                    </p>
                    <select
                      value={accountId}
                      onChange={(e) => setAccountId(e.target.value)}
                      aria-label="Account"
                      className="min-h-10 w-full rounded-lg border border-[var(--cp-line)] bg-[var(--cp-surface)] px-3 py-2 text-sm text-[var(--cp-ink)]"
                    >
                      <option value="">No account</option>
                      {accounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.bankName}
                          {a.nickname ? ` (${a.nickname})` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  {type === "expense" && (
                    <div>
                      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--cp-ink-3)]">
                        Paid via
                      </p>
                      <select
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        aria-label="Payment method"
                        className="min-h-10 w-full rounded-lg border border-[var(--cp-line)] bg-[var(--cp-surface)] px-3 py-2 text-sm text-[var(--cp-ink)]"
                      >
                        {PAYMENT_METHODS.map((m) => (
                          <option key={m.value} value={m.value}>
                            {m.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Amount keypad — bottom of the sheet, where the OS keyboard
                    used to sit. Everything above it stays visible and
                    tappable, which is the whole point: no field is ever
                    occluded while an amount is being entered. */}
                {showPad && <NumberPad onKey={pressPadKey} />}

                {/* Save — and, when it is disabled by F7, why. */}
                {amountValue !== null && entryProblem && (
                  <p role="status" className="text-center text-[12px] font-medium text-[var(--cp-rose)]">
                    {entryProblem.message}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={!canSave || mutation.isPending}
                  className={cn(
                    "h-11 w-full rounded-[9px] text-sm font-semibold text-white shadow-[var(--cp-shadow-base)] transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-40 disabled:active:scale-100",
                    accent === "emerald"
                      ? "bg-[var(--cp-emerald)]"
                      : "bg-[var(--cp-rose)]",
                  )}
                >
                  {mutation.isPending
                    ? "Saving..."
                    : type === "income"
                      ? "Save Income"
                      : "Save Expense"}
                </button>
              </form>
            </motion.div>
          </div>
    </div>
  );

  // The AnimatePresence is keyed on the branch, not just its child. Swapping
  // the child's key inside one AnimatePresence makes it hold the outgoing
  // branch until that branch finishes exiting — and the desktop dialog is a
  // plain element with no exit animation, so it never reports completion and
  // both amount fields stay in the tree. Keying the boundary discards the old
  // branch outright. The swap happens once, when the breakpoint resolves.
  return (
    <AnimatePresence key={isMobile ? "mobile" : "desktop"}>
      {open && (isMobile ? mobileScreen : desktopDialog)}
    </AnimatePresence>
  );
}
